// Hover tooltip copy for the skill bar's Q/W/E/R buttons. Pure: hud.ts owns the element and only assigns
// the markup this returns when it differs from what's already shown.
import { SKILLS } from '../core/skills.js';
import { ARTIFACTS } from '../core/artifacts.js';
import type { HeroKind } from '../core/heroes.js';
import type { ArtifactLine, SkillKey, ArtifactSlotKey } from '../core/commands.js';
import { skillDesc } from './heroInfo.js';
import { t } from './i18n/index.js';
import { artifactName, relicName, skillName } from './i18n/names.js';
import { lineDesc, vialPct, VIAL_RATE_PCT, relicDesc, RELIC_COST, iconImg } from './shop.js';
import { RELICS } from '../core/relics.js';
import type { RelicLine } from '../core/commands.js';

type Targeting = 'instant' | 'direction' | 'point' | 'charge' | 'ally' | 'enemy';
const targeting = (kind: Targeting) => t(`target.${kind}` as const);

export const emptySlotTip = () => t('tip.emptySlot');

/** `cdLeft`: seconds of cooldown still running, 0 when ready. */
export function skillTipHtml(kind: HeroKind, key: SkillKey, cdLeft: number): string {
  const s = (SKILLS as any)[kind]?.[key];
  if (!s) return tipHtml(key, t('tip.empty'), [], null, 0);
  return tipHtml(key, skillName(kind, key), [targeting(s.targeting), t('common.cooldown', { cd: s.cd })], skillDesc(kind, key), cdLeft);
}

/** `charge`: Склянка крови's current charge as a share of max hp (core's vialCharge), ignored for other lines. */
export function artifactTipHtml(slot: ArtifactSlotKey, line: ArtifactLine | null, cdLeft: number, charge = 0): string {
  if (!line) return tipHtml(slot, emptySlotTip(), [], null, 0);
  if (line === 'bloodvial') {
    const def = ARTIFACTS.bloodvial;
    return tipHtml(slot, artifactName(line), [targeting(def.targeting)], lineDesc(line), 0, line)
      + `<div class="tip-cd">${t('tip.vialCharge', { pct: vialPct(charge), max: Math.round(def.heal * 100), rate: VIAL_RATE_PCT })}</div>`;
  }
  const def = ARTIFACTS[line] as any;
  const meta = def.kind === 'passive' ? [t('tip.worksItself')] : [targeting(def.targeting), t('common.cooldown', { cd: def.cd })];
  return tipHtml(slot, artifactName(line), meta, lineDesc(line), def.kind === 'passive' ? 0 : cdLeft, line);
}

export const emptyRelicTip = () => t('tip.noRelic', { cost: RELIC_COST });

/** The T slot: the team's relic, `cdLeft` its shared cooldown. */
export function relicTipHtml(line: RelicLine | null, cdLeft: number): string {
  if (!line) return tipHtml('T', emptyRelicTip(), [], null, 0);
  const def = RELICS[line];
  const meta = [t('tip.teamRelic'), def.kind === 'passive' ? t('tip.relicWorksItself') : t('tip.sharedCooldown', { cd: def.cd })];
  return tipHtml('T', relicName(line), meta, relicDesc(line), def.kind === 'passive' ? 0 : cdLeft, line)
    + `<div class="tip-block">${t('tip.relicLost')}</div>`;
}

function tipHtml(key: string, title: string, meta: string[], desc: string | null, cdLeft: number, icon?: ArtifactLine | RelicLine): string {
  return `<div class="tip-head"><b>${key}</b>${icon ? iconImg(icon, 'tip-ico', 28) : ''}<span>${title}</span></div>`
    + (meta.length ? `<div class="tip-meta">${meta.join(' · ')}</div>` : '')
    + (desc ? `<p>${desc}</p>` : '')
    + (cdLeft > 0 ? `<div class="tip-cd">${t('tip.cdLeft', { s: Math.ceil(cdLeft) })}</div>` : '');
}
